import { useState, useEffect, useRef } from 'react'
import DateRangePicker from './DateRangePicker'
import DailyTimeChart from './DailyTimeChart'
import DetailedWorklogsTable from './DetailedWorklogsTable'
import MultiSelectDropdown from './MultiSelectDropdown'
import GroupBySelector from './GroupBySelector'
import GroupedWorklogsView from './GroupedWorklogsView'
import GroupedDataPieChart from './GroupedDataPieChart'
import useReports from '../hooks/useReports'
import AutomatedReportsSection from './AutomatedReportsSection'
import styles from './ReportsSection.module.css'
import { moment, formatDisplayDate, getDateRangeForPreset } from '../utils/dateUtils'
import { isFeatureAvailable } from '../utils/featureGate'

export default function ReportsSection({ activeSubSection, onSubSectionChange, viewMode = 'summary', onViewModeChange, currentWorkspace }) {
    // Applied filters - used for fetching data
    const [appliedFilters, setAppliedFilters] = useState({
        clients: [], // Changed to array for multiselect
        team: [], // Already array for multiselect
        projects: [], // Changed to array for multiselect
        tasks: [] // Changed to array for multiselect
    })

    // Staging filters - changed in UI but not yet applied
    const [stagedFilters, setStagedFilters] = useState({
        clients: [],
        team: [],
        projects: [],
        tasks: []
    })
    
    const [activeFilterSet, setActiveFilterSet] = useState('New')
    const [showSaveDialog, setShowSaveDialog] = useState(false)
    const [newFilterName, setNewFilterName] = useState('')
    const [filtersCollapsed, setFiltersCollapsed] = useState(false)
    const [showFilterDropdown, setShowFilterDropdown] = useState(false)
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
    const [filterToDelete, setFilterToDelete] = useState(null)
    // Initialize date range with proper default dates for "This week"
    const getInitialDateRange = () => {
        const today = moment()
        const startOfWeek = today.clone().startOf('week') // Sunday
        const endOfWeek = today.clone().endOf('week') // Saturday

        return {
            from: startOfWeek.format('YYYY-MM-DD'),
            to: endOfWeek.format('YYYY-MM-DD'),
            preset: 'This week'
        }
    }
    
    const [dateRange, setDateRange] = useState(getInitialDateRange)
    const [showExportMenu, setShowExportMenu] = useState(false)
    const exportMenuRef = useRef(null)
    const filterDropdownRef = useRef(null)
    const [groupByFields, setGroupByFields] = useState(['project'])
    const [groupedData, setGroupedData] = useState(null)
    const [groupedLoading, setGroupedLoading] = useState(false)
    const [selectedWorklog, setSelectedWorklog] = useState(null)

    // Rounding toggle state for reports
    const [enableRounding, setEnableRounding] = useState(false)
    // TODO: Implement rounding functionality when enableRounding is true
    // Apply rounding to time calculations in charts, tables, and exports

    // Billable column display toggle
    const [showBillableColumn, setShowBillableColumn] = useState(false)

    // Accordion state for sections
    const [isDailyChartExpanded, setIsDailyChartExpanded] = useState(true)
    const [isGroupedReportsExpanded, setIsGroupedReportsExpanded] = useState(true)

    // Use the reports hook for API data
    const { data, loading, error, savedFilters, fetchReports, fetchFilterOptions, exportReports, fetchSavedFilters, saveSavedFilter, deleteSavedFilter, fetchGroupedReports } = useReports()
    
    // Get filter options from API or use empty defaults
    const filterOptions = data.filters || {
        clients: [],
        team: [],
        projects: [],
        tasks: []
    }

    // Filter projects based on selected clients
    const getFilteredProjectOptions = () => {
        if (!filterOptions.projects || !Array.isArray(filterOptions.projects)) {
            return []
        }

        // If no clients are selected, show all projects
        if (!stagedFilters.clients || stagedFilters.clients.length === 0) {
            return filterOptions.projects
        }

        // If all clients are selected, show all projects
        const totalClients = filterOptions.clients ? filterOptions.clients.length : 0
        if (stagedFilters.clients.length === totalClients) {
            return filterOptions.projects
        }

        // Filter projects to only show those belonging to selected clients
        return filterOptions.projects.filter(project => {
            // Convert client IDs to strings for comparison (to handle mixed types)
            const selectedClientIds = stagedFilters.clients.map(id => String(id))
            // project.client_id should match one of the selected clients
            const projectClientId = String(project.client_id)
            return selectedClientIds.includes(projectClientId)
        })
    }

    // Filter tasks based on hierarchical selection (client → project → task)
    const getFilteredTaskOptions = () => {
        if (!filterOptions.tasks || !Array.isArray(filterOptions.tasks)) {
            return []
        }

        // Priority 1: If projects are selected, show tasks from those projects only
        if (stagedFilters.projects && stagedFilters.projects.length > 0) {
            return filterOptions.tasks.filter(task => {
                return stagedFilters.projects.includes(task.project_id)
            })
        }

        // Priority 2: If clients are selected (but no projects), show tasks from ALL projects of those clients
        if (stagedFilters.clients && stagedFilters.clients.length > 0) {
            // If all clients are selected, show all tasks
            const totalClients = filterOptions.clients ? filterOptions.clients.length : 0
            if (stagedFilters.clients.length === totalClients) {
                return filterOptions.tasks
            }

            // Filter tasks by client_id
            return filterOptions.tasks.filter(task => {
                const selectedClientIds = stagedFilters.clients.map(id => String(id))
                return selectedClientIds.includes(String(task.client_id))
            })
        }

        // Priority 3: If neither clients nor projects are selected, show all tasks
        return filterOptions.tasks
    }

    const filteredProjectOptions = getFilteredProjectOptions()
    const filteredTaskOptions = getFilteredTaskOptions()

    // Auto-clear invalid project selections when clients change
    useEffect(() => {
        if (stagedFilters.projects && stagedFilters.projects.length > 0) {
            // Check if any selected projects are no longer valid for the selected clients
            const validProjectIds = filteredProjectOptions.map(project => project.id)
            const validSelectedProjects = stagedFilters.projects.filter(projectId => validProjectIds.includes(projectId))
            
            // If some projects became invalid, update the selection
            if (validSelectedProjects.length !== stagedFilters.projects.length) {
                setActiveFilters(prev => ({
                    ...prev,
                    projects: validSelectedProjects
                }))
            }
        }
    }, [stagedFilters.clients, filteredProjectOptions.length]) // Use length to avoid infinite loops

    // Auto-clear invalid task selections when clients or projects change
    useEffect(() => {
        if (stagedFilters.tasks && stagedFilters.tasks.length > 0) {
            // Check if any selected tasks are no longer valid for the current filter hierarchy
            const validTaskIds = filteredTaskOptions.map(task => task.id)
            const validSelectedTasks = stagedFilters.tasks.filter(taskId => validTaskIds.includes(taskId))
            
            // If some tasks became invalid, update the selection
            if (validSelectedTasks.length !== stagedFilters.tasks.length) {
                setActiveFilters(prev => ({
                    ...prev,
                    tasks: validSelectedTasks
                }))
            }
        }
    }, [stagedFilters.clients, stagedFilters.projects, filteredTaskOptions.length]) // Monitor both clients and projects

    // Initialize date range on mount
    useEffect(() => {
        const today = new Date()
        const startOfWeek = new Date(today)
        startOfWeek.setDate(today.getDate() - today.getDay())
        const endOfWeek = new Date(startOfWeek)
        endOfWeek.setDate(startOfWeek.getDate() + 6)
        
        setDateRange({
            from: startOfWeek.toISOString().split('T')[0],
            to: endOfWeek.toISOString().split('T')[0],
            preset: 'This week'
        })
    }, [])

    // Fetch filter options and saved filters on mount
    useEffect(() => {
        fetchFilterOptions()
        fetchSavedFilters()
    }, [fetchFilterOptions, fetchSavedFilters])

    // Load initial reports data immediately when component mounts
    useEffect(() => {
        if (activeSubSection === 'dashboard') {
            const params = {
                ...appliedFilters,
                start_date: dateRange.from,
                end_date: dateRange.to,
                enable_rounding: enableRounding,
                show_billable_column: showBillableColumn
            }
            fetchReports(params)
        }
    }, []) // Empty dependency array - only run on mount

    // Fetch reports data when date range changes (but not when filters change)
    useEffect(() => {
        if (dateRange.from && dateRange.to && activeSubSection === 'dashboard') {
            const params = {
                ...appliedFilters,
                start_date: dateRange.from,
                end_date: dateRange.to,
                enable_rounding: enableRounding,
                show_billable_column: showBillableColumn
            }
            fetchReports(params)
        }
    }, [dateRange, fetchReports, activeSubSection, appliedFilters])

    // Refetch reports when rounding option changes
    useEffect(() => {
        if (activeSubSection === 'dashboard') {
            const params = {
                ...appliedFilters,
                start_date: dateRange.from,
                end_date: dateRange.to,
                enable_rounding: enableRounding,
                show_billable_column: showBillableColumn
            }
            fetchReports(params)
        }
    }, [enableRounding, fetchReports, activeSubSection, appliedFilters, dateRange.from, dateRange.to, showBillableColumn])

    // Fetch grouped reports data when group-by fields or date range change (but not when filters change)
    useEffect(() => {
        if (dateRange.from && dateRange.to && activeSubSection === 'dashboard' && groupByFields.length > 0) {
            const params = {
                ...appliedFilters,
                start_date: dateRange.from,
                end_date: dateRange.to
            }
            
            setGroupedLoading(true)
            fetchGroupedReports(params, groupByFields)
                .then(result => {
                    setGroupedData(result.grouped_data)
                })
                .catch(err => {
                    console.error('Failed to fetch grouped reports:', err)
                })
                .finally(() => {
                    setGroupedLoading(false)
                })
        }
    }, [dateRange, groupByFields, fetchGroupedReports, activeSubSection])

    // Refresh grouped data when switching from detailed back to summary view
    useEffect(() => {
        if (viewMode === 'summary' && activeSubSection === 'dashboard' && groupByFields.length > 0 && dateRange.from && dateRange.to) {
            const params = {
                ...appliedFilters,
                start_date: dateRange.from,
                end_date: dateRange.to
            }
            
            setGroupedLoading(true)
            fetchGroupedReports(params, groupByFields)
                .then(result => {
                    setGroupedData(result.grouped_data)
                })
                .catch(err => {
                    console.error('Failed to fetch grouped reports:', err)
                })
                .finally(() => {
                    setGroupedLoading(false)
                })
        }
    }, [viewMode, appliedFilters, dateRange.from, dateRange.to, activeSubSection, groupByFields, fetchGroupedReports])

    // Close export menu and filter dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (exportMenuRef.current && !exportMenuRef.current.contains(event.target)) {
                setShowExportMenu(false)
            }
            if (filterDropdownRef.current && !filterDropdownRef.current.contains(event.target)) {
                setShowFilterDropdown(false)
            }
        }

        document.addEventListener('mousedown', handleClickOutside)
        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [])

    const applyFilters = () => {
        // Commit staged filters to applied filters
        setAppliedFilters(stagedFilters)

        if (dateRange.from && dateRange.to) {
            const params = {
                ...stagedFilters,
                start_date: dateRange.from,
                end_date: dateRange.to,
                enable_rounding: enableRounding,
                show_billable_column: showBillableColumn
            }
            fetchReports(params)

            // Also refresh grouped reports if groupBy fields are selected
            if (groupByFields.length > 0) {
                setGroupedLoading(true)
                fetchGroupedReports(params, groupByFields)
                    .then(result => {
                        setGroupedData(result.grouped_data)
                    })
                    .catch(err => {
                        console.error('Failed to fetch grouped reports:', err)
                    })
                    .finally(() => {
                        setGroupedLoading(false)
                    })
            }
        }
    }

    const clearFilters = () => {
        const emptyFilters = {
            clients: [], // Reset to empty array
            team: [], // Reset to empty array
            projects: [], // Reset to empty array
            tasks: [] // Reset to empty array
        }
        setStagedFilters(emptyFilters)
        setAppliedFilters(emptyFilters)
        setActiveFilterSet('New')
        // Clear grouping as well
        setGroupByFields([])
        setGroupedData(null)
    }

    const loadSavedFilter = (filterName) => {
        if (filterName === 'New') {
            clearFilters()
            // Clear grouping as well
            setGroupByFields([])
            setGroupedData(null)
            setActiveFilterSet('New')
            return
        }

        const savedFilter = savedFilters.find(f => f.name === filterName)

        if (savedFilter) {
            // Load filter criteria
            const loadedFilters = {
                clients: Array.isArray(savedFilter.filters?.clients) ? savedFilter.filters.clients : (savedFilter.filters?.clients ? [savedFilter.filters.clients] : []),
                team: Array.isArray(savedFilter.filters?.team) ? savedFilter.filters.team : (savedFilter.filters?.team ? [savedFilter.filters.team] : []),
                projects: Array.isArray(savedFilter.filters?.projects) ? savedFilter.filters.projects : (savedFilter.filters?.projects ? [savedFilter.filters.projects] : []),
                tasks: Array.isArray(savedFilter.filters?.tasks) ? savedFilter.filters.tasks : (savedFilter.filters?.tasks ? [savedFilter.filters.tasks] : [])
            }
            setStagedFilters(loadedFilters)
            setAppliedFilters(loadedFilters)

            // Load additional options
            if (savedFilter.filters?.show_billable_column !== undefined) {
                setShowBillableColumn(savedFilter.filters.show_billable_column)
            }
            if (savedFilter.filters?.enable_rounding !== undefined) {
                setEnableRounding(savedFilter.filters.enable_rounding)
            }
            // Calculate the date range to use
            let dateRangeToUse = dateRange
            if (savedFilter.filters?.date_range_preset) {

                // If it's a custom range, use the saved dates; otherwise recalculate from preset
                if (savedFilter.filters.date_range_preset === 'Custom') {
                    // Use saved specific dates for custom ranges
                    dateRangeToUse = {
                        from: savedFilter.filters.start_date,
                        to: savedFilter.filters.end_date,
                        preset: 'Custom'
                    }
                } else {
                    // Recalculate dates from preset (e.g., "Last month" always means last month from today)
                    const range = getDateRangeForPreset(savedFilter.filters.date_range_preset)
                    dateRangeToUse = {
                        from: range.from,
                        to: range.to,
                        preset: savedFilter.filters.date_range_preset
                    }
                }
                setDateRange(dateRangeToUse)
            }

            // Load groupBy fields
            const savedGroupByFields = savedFilter.group_by_fields || []
            setGroupByFields(savedGroupByFields)

            setActiveFilterSet(filterName)

            // If there are groupBy fields, fetch grouped data
            if (savedGroupByFields.length > 0 && dateRangeToUse.from && dateRangeToUse.to) {
                const filters = {
                    clients: Array.isArray(savedFilter.filters?.clients) ? savedFilter.filters.clients : (savedFilter.filters?.clients ? [savedFilter.filters.clients] : []),
                    team: Array.isArray(savedFilter.filters?.team) ? savedFilter.filters.team : (savedFilter.filters?.team ? [savedFilter.filters.team] : []),
                    projects: Array.isArray(savedFilter.filters?.projects) ? savedFilter.filters.projects : (savedFilter.filters?.projects ? [savedFilter.filters.projects] : []),
                    tasks: Array.isArray(savedFilter.filters?.tasks) ? savedFilter.filters.tasks : (savedFilter.filters?.tasks ? [savedFilter.filters.tasks] : [])
                }

                const params = {
                    ...filters,
                    start_date: dateRangeToUse.from,
                    end_date: dateRangeToUse.to
                }
                
                setGroupedLoading(true)
                fetchGroupedReports(params, savedGroupByFields)
                    .then(result => {
                        setGroupedData(result.grouped_data)
                    })
                    .catch(err => {
                        console.error('Failed to fetch grouped reports:', err)
                    })
                    .finally(() => {
                        setGroupedLoading(false)
                    })
            } else {
                // Clear grouped data if no grouping
                setGroupedData(null)
            }
        }
    }

    const getActiveFilterCount = () => {
        return Object.values(stagedFilters).filter(filter => {
            if (Array.isArray(filter)) {
                return filter.length > 0
            }
            return filter !== ''
        }).length
    }


    const handleExport = async (format) => {
        setShowExportMenu(false)
        
        const params = {
            ...stagedFilters,
            start_date: dateRange.from,
            end_date: dateRange.to,
            report_type: viewMode, // Pass the current view mode as report type
            group_by: groupByFields, // Include the grouping selection
            enable_rounding: enableRounding,
            show_billable_column: showBillableColumn
        }
        
        await exportReports(format, params)
    }

    const handleDeleteFilter = (filter) => {
        setFilterToDelete(filter)
        setShowDeleteConfirm(true)
        setShowFilterDropdown(false)
    }

    const confirmDeleteFilter = async () => {
        if (filterToDelete) {
            try {
                await deleteSavedFilter(filterToDelete.id)
                // If the deleted filter was active, reset to New
                if (activeFilterSet === filterToDelete.name) {
                    setActiveFilterSet('New')
                    clearFilters()
                }
            } catch (err) {
                console.error('Failed to delete filter:', err)
            } finally {
                // Always close dialog regardless of success or failure
                setShowDeleteConfirm(false)
                setFilterToDelete(null)
            }
        }
    }

    const handleGroupClick = (filterHierarchy) => {
        // Build new filters based on the hierarchy
        const newFilters = { ...stagedFilters }
        
        filterHierarchy.forEach(filter => {
            switch (filter.field) {
                case 'user':
                case 'user_name':
                    // Find user ID from filter options
                    const user = filterOptions.team?.find(u => u.name === filter.value)
                    if (user && !newFilters.team.includes(user.id)) {
                        newFilters.team = [...newFilters.team, user.id]
                    }
                    break
                case 'project':
                case 'project_name':
                    // Find project ID from filter options
                    const project = filterOptions.projects?.find(p => p.name === filter.value)
                    if (project && !newFilters.projects.includes(project.id)) {
                        newFilters.projects = [...newFilters.projects, project.id]
                    }
                    break
                case 'client':
                case 'client_name':
                    // Find client ID from filter options
                    const client = filterOptions.clients?.find(c => c.name === filter.value)
                    if (client && !newFilters.clients.includes(client.id)) {
                        newFilters.clients = [...newFilters.clients, client.id]
                    }
                    break
                case 'task':
                case 'task_name':
                    // Find task ID from filter options
                    const task = filterOptions.tasks?.find(t => t.name === filter.value)
                    if (task && !newFilters.tasks.includes(task.id)) {
                        newFilters.tasks = [...newFilters.tasks, task.id]
                    }
                    break
            }
        })
        
        // Update the filters in the UI
        setActiveFilters(newFilters)
        setActiveFilterSet('New') // Reset to New when filters are modified
        
        // Switch to detailed view
        onViewModeChange('detailed')
        
        // Immediately fetch detailed reports with the new filters
        const params = {
            ...newFilters,
            start_date: dateRange.from,
            end_date: dateRange.to,
            page: 1
        }
        fetchReports(params)
    }

    const renderSubSectionContent = () => {
        switch(activeSubSection) {
            case 'dashboard':
                return (
                    <div className={styles.dashboardSplitView}>
                        {/* Left Panel - Filters */}
                        <div className={`${styles.filtersPanel} ${filtersCollapsed ? styles.collapsed : ''}`}>
                            <div className={styles.filtersPanelHeader}>
                                <div className={styles.filtersPanelTitle}>
                                    <h3>Filters</h3>
                                    <button 
                                        className={styles.collapseButton}
                                        onClick={() => setFiltersCollapsed(!filtersCollapsed)}
                                        aria-label={filtersCollapsed ? 'Expand filters' : 'Collapse filters'}
                                    >
                                        {filtersCollapsed ? '▼' : '▲'}
                                    </button>
                                </div>
                                {getActiveFilterCount() > 0 && (
                                    <button 
                                        onClick={clearFilters} 
                                        className={styles.clearFiltersButton}
                                        title="Clear all filters"
                                    >
                                        Clear
                                    </button>
                                )}
                            </div>
                            
                            {!filtersCollapsed && (
                                <>
                                    <div className={styles.filtersContent}>
                                        {/* Saved Filters Dropdown */}
                                        <div className={styles.savedFiltersSection}>
                                            <label className={styles.dropdownLabel}>Filter Set:</label>
                                            <div className={styles.customDropdown} ref={filterDropdownRef}>
                                                <button 
                                                    className={styles.dropdownToggle}
                                                    onClick={() => setShowFilterDropdown(!showFilterDropdown)}
                                                >
                                                    {activeFilterSet}
                                                    <span className={styles.dropdownArrow}>▼</span>
                                                </button>
                                                
                                                {showFilterDropdown && (
                                                    <div className={styles.dropdownMenu}>
                                                        <div 
                                                            className={styles.dropdownItem}
                                                            onClick={() => {
                                                                loadSavedFilter('New')
                                                                setShowFilterDropdown(false)
                                                            }}
                                                        >
                                                            <span className={styles.filterName}>
                                                                New
                                                            </span>
                                                        </div>
                                                        {savedFilters.map(filter => (
                                                            <div 
                                                                key={filter.id}
                                                                className={styles.dropdownItem}
                                                            >
                                                                <span 
                                                                    className={styles.filterName}
                                                                    onClick={() => {
                                                                        loadSavedFilter(filter.name)
                                                                        setShowFilterDropdown(false)
                                                                    }}
                                                                >
                                                                    {filter.name}
                                                                </span>
                                                                <button 
                                                                    className={styles.deleteFilterButton}
                                                                    onClick={(e) => {
                                                                        e.stopPropagation()
                                                                        handleDeleteFilter(filter)
                                                                    }}
                                                                    title="Delete filter"
                                                                >
                                                                    🗑️
                                                                </button>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {/* Filter Sections */}
                                        {/* Clients MultiSelect Filter */}
                                        <MultiSelectDropdown
                                            label="Clients"
                                            options={filterOptions.clients}
                                            selectedValues={stagedFilters.clients}
                                            onChange={(values) => setStagedFilters(prev => ({
                                                ...prev,
                                                clients: values
                                            }))}
                                            placeholder="All Clients"
                                            searchPlaceholder="Search clients..."
                                            className={styles.savedFiltersSection}
                                        />
                                        
                                        {/* Team MultiSelect Filter */}
                                        <MultiSelectDropdown
                                            label="Team"
                                            options={filterOptions.team}
                                            selectedValues={stagedFilters.team}
                                            onChange={(values) => setStagedFilters(prev => ({
                                                ...prev,
                                                team: values
                                            }))}
                                            placeholder="All Team"
                                            searchPlaceholder="Search team members..."
                                            className={styles.savedFiltersSection}
                                        />
                                        
                                        {/* Projects MultiSelect Filter */}
                                        <MultiSelectDropdown
                                            label="Projects"
                                            options={filteredProjectOptions}
                                            selectedValues={stagedFilters.projects}
                                            onChange={(values) => setStagedFilters(prev => ({
                                                ...prev,
                                                projects: values
                                            }))}
                                            placeholder="All Projects"
                                            searchPlaceholder="Search projects..."
                                            className={styles.savedFiltersSection}
                                        />
                                        
                                        {/* Tasks MultiSelect Filter */}
                                        <MultiSelectDropdown
                                            label="Tasks"
                                            options={filteredTaskOptions}
                                            selectedValues={stagedFilters.tasks}
                                            onChange={(values) => setStagedFilters(prev => ({
                                                ...prev,
                                                tasks: values
                                            }))}
                                            placeholder={stagedFilters.projects.length > 0 ? "All Tasks in selected projects" : "All Tasks"}
                                            searchPlaceholder="Search tasks..."
                                            className={styles.savedFiltersSection}
                                        />

                                        {/* Billable Column Toggle */}
                                        <div className={styles.billableToggleSection}>
                                            <label className={styles.billableToggleLabel}>
                                                <input
                                                    type="checkbox"
                                                    checked={showBillableColumn}
                                                    onChange={(e) => setShowBillableColumn(e.target.checked)}
                                                    className={styles.billableToggleCheckbox}
                                                />
                                                <span className={styles.billableToggleSlider}></span>
                                                <span className={styles.billableToggleText}>Show Billable Column</span>
                                            </label>
                                        </div>
                                    </div>

                                    {/* Filter Actions */}
                                    <div className={styles.filterActions}>
                                        <div className={styles.filterButtonGroup}>
                                            <button onClick={applyFilters} className={styles.applyButton}>
                                                Apply Filter
                                            </button>
                                            <button onClick={() => setShowSaveDialog(true)} className={styles.saveButton}>
                                                {activeFilterSet === 'New' ? 'Save Filter' : 'Save As'}
                                            </button>
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>

                        {/* Right Panel - Summary or Detailed View */}
                        <div className={styles.detailsPanel}>
                            {loading && (
                                <div className={styles.loadingState}>
                                    <p>Loading reports...</p>
                                </div>
                            )}
                            
                            {error && (
                                <div className={styles.errorState}>
                                    <p>Error loading reports: {error}</p>
                                    <button onClick={applyFilters} className={styles.retryButton}>
                                        Retry
                                    </button>
                                </div>
                            )}
                            
                            {!loading && !error && (
                                viewMode === 'summary' ? (
                                    <div className={styles.dashboardGrid}>
                                        {/* Grouped Reports Section - Accordion */}
                                        <div className={styles.accordionSection}>
                                            <div
                                                className={styles.accordionHeader}
                                                onClick={() => setIsGroupedReportsExpanded(!isGroupedReportsExpanded)}
                                            >
                                                <div className={styles.accordionTitle}>
                                                    <span className={`${styles.accordionIcon} ${isGroupedReportsExpanded ? styles.expanded : ''}`}>
                                                        ▶
                                                    </span>
                                                    <h3>Grouped Reports</h3>
                                                </div>
                                            </div>
                                            {isGroupedReportsExpanded && (
                                                <div className={styles.accordionContent}>
                                                    <div className={styles.groupedReportsSection}>
                                            <div className={styles.groupBySelectorContainer}>
                                                <GroupBySelector
                                                    groupByFields={groupByFields}
                                                    onChange={setGroupByFields}
                                                />
                                            </div>

                                            {/* Grouped Data View */}
                                            {groupedLoading && (
                                                <div className={styles.loadingState}>
                                                    <p>Loading grouped reports...</p>
                                                </div>
                                            )}

                                            {!groupedLoading && groupByFields.length > 0 && (
                                                <div className={styles.groupedReportsContent}>
                                                    <div className={styles.groupedListSection}>
                                                        <GroupedWorklogsView
                                                            groupedData={groupedData}
                                                            groupByFields={groupByFields}
                                                            onWorklogClick={(worklog) => {
                                                                setSelectedWorklog(worklog)
                                                            }}
                                                            onGroupClick={handleGroupClick}
                                                        />
                                                    </div>
                                                    <div className={styles.groupedChartSection}>
                                                        <GroupedDataPieChart
                                                            groupedData={groupedData}
                                                            groupByFields={groupByFields}
                                                        />
                                                    </div>
                                                </div>
                                            )}

                                            {!groupedLoading && groupByFields.length === 0 && (
                                                <div className={styles.emptyGroupByState}>
                                                    <h3>Group Your Data</h3>
                                                    <p>Select one or more fields above to group your worklogs and see detailed breakdowns.</p>
                                                </div>
                                            )}
                                        </div>
                                                </div>
                                            )}
                                        </div>

                                        {/* Daily Time Tracking Chart - Accordion */}
                                        <div className={styles.accordionSection}>
                                            <div
                                                className={styles.accordionHeader}
                                                onClick={() => setIsDailyChartExpanded(!isDailyChartExpanded)}
                                            >
                                                <div className={styles.accordionTitle}>
                                                    <span className={`${styles.accordionIcon} ${isDailyChartExpanded ? styles.expanded : ''}`}>
                                                        ▶
                                                    </span>
                                                    <h3>Daily Time Tracking</h3>
                                                </div>
                                            </div>
                                            {isDailyChartExpanded && (
                                                <div className={styles.accordionContent}>
                                                    <DailyTimeChart
                                                        dateRange={dateRange}
                                                        filters={appliedFilters}
                                                        chartData={data.chart}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ) : (
                                    <DetailedWorklogsTable
                                        filters={appliedFilters}
                                        dateRange={dateRange}
                                        reportData={data.detailed}
                                        showBillableColumn={showBillableColumn}
                                        onPageChange={(page) => {
                                            const params = {
                                                ...appliedFilters,
                                                start_date: dateRange.from,
                                                end_date: dateRange.to,
                                                page: page,
                                                enable_rounding: enableRounding
                                            }
                                            fetchReports(params)
                                        }}
                                    />
                                )
                            )}
                        </div>
                    </div>
                )
            case 'automated-reports':
                return (
                    <AutomatedReportsSection />
                )
            default:
                return null
        }
    }

    return (
        <div className={styles.reportsSection}>
            {/* Secondary Menu Bar */}
            <div className={styles.secondaryMenu}>
                <nav className={styles.subNav}>
                    <button
                        className={`${styles.subNavItem} ${activeSubSection === 'dashboard' ? styles.active : ''}`}
                        onClick={() => onSubSectionChange('dashboard')}
                    >
                        Dashboard
                    </button>
                    {isFeatureAvailable('automated_reports', currentWorkspace) && (
                        <button
                            className={`${styles.subNavItem} ${activeSubSection === 'automated-reports' ? styles.active : ''}`}
                            onClick={() => onSubSectionChange('automated-reports')}
                        >
                            Automated Reports
                        </button>
                    )}
                </nav>
                
                {/* Center Controls - View Mode Split Button */}
                {activeSubSection === 'dashboard' && (
                    <div className={styles.centerControls}>
                        <div className={styles.viewModeSplitButton}>
                            <button
                                className={`${styles.splitButtonLeft} ${viewMode === 'summary' ? styles.active : ''}`}
                                onClick={() => onViewModeChange('summary')}
                            >
                                Summary
                            </button>
                            <button
                                className={`${styles.splitButtonRight} ${viewMode === 'detailed' ? styles.active : ''}`}
                                onClick={() => onViewModeChange('detailed')}
                            >
                                Detailed
                            </button>
                        </div>
                    </div>
                )}
                
                {/* Date Range Picker and Export */}
                {activeSubSection === 'dashboard' && (
                    <div className={styles.rightControls}>
                        {/* Rounding Toggle */}
                        <div className={styles.roundingToggle}>
                            <label className={styles.roundingLabel}>
                                <input
                                    type="checkbox"
                                    checked={enableRounding}
                                    onChange={(e) => setEnableRounding(e.target.checked)}
                                    className={styles.roundingCheckbox}
                                />
                                <span className={styles.roundingSlider}></span>
                                <span className={styles.roundingText}>Round time</span>
                            </label>
                        </div>
                        <DateRangePicker
                            dateRange={dateRange}
                            onDateRangeChange={setDateRange}
                        />
                        {/* Export Button */}
                        <div className={styles.exportSection} ref={exportMenuRef}>
                            <button 
                                className={styles.exportButton}
                                onClick={() => setShowExportMenu(!showExportMenu)}
                                aria-label="Export reports"
                            >
                                <span className={styles.exportIcon}>📊</span>
                                <span>Export</span>
                                <span className={styles.dropdownArrow}>▼</span>
                            </button>

                            {/* Export dropdown menu */}
                            {showExportMenu && (
                                <div className={styles.exportMenu}>
                                    <div className={styles.exportMenuHeader}>
                                        <span>Export Format</span>
                                    </div>
                                    <button
                                        className={styles.exportMenuItem}
                                        onClick={() => handleExport('PDF')}
                                    >
                                        <span className={styles.exportItemIcon}>📄</span>
                                        <div className={styles.exportItemContent}>
                                            <span className={styles.exportItemText}>PDF Document</span>
                                            <span className={styles.exportItemDesc}>Portable format for sharing</span>
                                        </div>
                                    </button>
                                    <button
                                        className={styles.exportMenuItem}
                                        onClick={() => handleExport('XLS')}
                                    >
                                        <span className={styles.exportItemIcon}>📊</span>
                                        <div className={styles.exportItemContent}>
                                            <span className={styles.exportItemText}>Excel Spreadsheet</span>
                                            <span className={styles.exportItemDesc}>Editable spreadsheet format</span>
                                        </div>
                                    </button>
                                    <button
                                        className={styles.exportMenuItem}
                                        onClick={() => handleExport('CSV')}
                                    >
                                        <span className={styles.exportItemIcon}>📋</span>
                                        <div className={styles.exportItemContent}>
                                            <span className={styles.exportItemText}>CSV Data</span>
                                            <span className={styles.exportItemDesc}>Raw data for analysis</span>
                                        </div>
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* Sub-section Content */}
            <div className={styles.contentArea}>
                {renderSubSectionContent()}
            </div>

            {/* Delete Confirmation Dialog */}
            {showDeleteConfirm && (
                <div className={styles.saveDialog}>
                    <div className={styles.saveDialogContent}>
                        <h4>Delete Filter</h4>
                        <p>Are you sure you want to delete the filter "{filterToDelete?.name}"? This action cannot be undone.</p>
                        <div className={styles.saveDialogActions}>
                            <button 
                                onClick={() => {
                                    setShowDeleteConfirm(false)
                                    setFilterToDelete(null)
                                }}
                                className={styles.cancelButton}
                            >
                                Cancel
                            </button>
                            <button 
                                onClick={confirmDeleteFilter}
                                className={styles.deleteButton}
                            >
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Save Dialog */}
            {showSaveDialog && (
                <div className={styles.saveDialog}>
                    <div className={styles.saveDialogContent}>
                        <h4>Save Filter Set</h4>
                        <input
                            type="text"
                            value={newFilterName}
                            onChange={(e) => setNewFilterName(e.target.value)}
                            placeholder="Enter filter name..."
                            className={styles.saveInput}
                            autoFocus
                        />
                        <div className={styles.saveDialogActions}>
                            <button 
                                onClick={() => setShowSaveDialog(false)}
                                className={styles.cancelButton}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={async () => {
                                    if (newFilterName.trim()) {
                                        try {
                                            await saveSavedFilter(
                                                newFilterName,
                                                appliedFilters,
                                                groupByFields,
                                                {
                                                    showBillableColumn: showBillableColumn,
                                                    enableRounding: enableRounding,
                                                    dateRangePreset: dateRange.preset || 'This week',
                                                    startDate: dateRange.from,
                                                    endDate: dateRange.to
                                                }
                                            )
                                            setActiveFilterSet(newFilterName)
                                            setShowSaveDialog(false)
                                            setNewFilterName('')
                                        } catch (err) {
                                            // Error is already handled in the hook
                                            console.error('Failed to save filter:', err)
                                        }
                                    }
                                }}
                                className={styles.confirmSaveButton}
                                disabled={!newFilterName.trim()}
                            >
                                Save
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Worklog Detail Modal */}
            {selectedWorklog && (
                <div className={styles.worklogModal} onClick={() => setSelectedWorklog(null)}>
                    <div className={styles.worklogModalContent} onClick={(e) => e.stopPropagation()}>
                        <div className={styles.worklogModalHeader}>
                            <h3>Worklog Details</h3>
                            <button 
                                className={styles.closeModalButton}
                                onClick={() => setSelectedWorklog(null)}
                                aria-label="Close modal"
                            >
                                ×
                            </button>
                        </div>
                        
                        <div className={styles.worklogModalBody}>
                            <div className={styles.worklogDetail}>
                                <label>User:</label>
                                <span>{selectedWorklog.user?.name}</span>
                            </div>
                            
                            <div className={styles.worklogDetail}>
                                <label>Description:</label>
                                <span>{selectedWorklog.description || 'No description'}</span>
                            </div>
                            
                            <div className={styles.worklogDetail}>
                                <label>Project:</label>
                                <span>{selectedWorklog.project?.name}</span>
                            </div>
                            
                            {selectedWorklog.task?.name && (
                                <div className={styles.worklogDetail}>
                                    <label>Task:</label>
                                    <span>{selectedWorklog.task.name}</span>
                                </div>
                            )}
                            
                            <div className={styles.worklogDetail}>
                                <label>Date:</label>
                                <span>{formatDisplayDate(selectedWorklog.date)}</span>
                            </div>
                            
                            <div className={styles.worklogDetail}>
                                <label>Duration:</label>
                                <span className={styles.durationValue}>{selectedWorklog.duration_formatted}</span>
                            </div>
                            
                            {selectedWorklog.start_time && (
                                <div className={styles.worklogDetail}>
                                    <label>Start Time:</label>
                                    <span>{selectedWorklog.start_time}</span>
                                </div>
                            )}
                            
                            {selectedWorklog.end_time && (
                                <div className={styles.worklogDetail}>
                                    <label>End Time:</label>
                                    <span>{selectedWorklog.end_time}</span>
                                </div>
                            )}
                            
                            <div className={styles.worklogDetail}>
                                <label>Billable:</label>
                                <span className={selectedWorklog.billable ? styles.billableYes : styles.billableNo}>
                                    {selectedWorklog.billable ? 'Yes' : 'No'}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}